import React, { useState, useEffect } from 'react';
import { Settings, Save, Building, Mail, Phone, MapPin, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin panel translations
const t = {
  en: {
    title: 'Business Settings',
    subtitle: 'Configure your business details for invoices and legal documents',
    businessInfo: 'Business Information',
    businessName: 'Business Name',
    legalName: 'Legal Name (for invoices)',
    cifNif: 'CIF/NIF',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    email: 'Email',
    phone: 'Phone',
    website: 'Website',
    bankDetails: 'Bank Details',
    iban: 'IBAN',
    bankName: 'Bank Name',
    invoiceSettings: 'Invoice Settings',
    invoicePrefix: 'Invoice Prefix',
    nextInvoiceNumber: 'Next Invoice Number',
    save: 'Save Settings',
    saving: 'Saving...',
    saved: 'Settings saved successfully',
    error: 'Error saving settings',
    lastUpdated: 'Last updated'
  },
  es: {
    title: 'Configuración de Empresa',
    subtitle: 'Configura los datos de tu empresa para facturas y documentos legales',
    businessInfo: 'Información de la Empresa',
    businessName: 'Nombre Comercial',
    legalName: 'Razón Social (para facturas)',
    cifNif: 'CIF/NIF',
    address: 'Dirección',
    city: 'Ciudad',
    postalCode: 'Código Postal',
    country: 'País',
    email: 'Email',
    phone: 'Teléfono',
    website: 'Sitio Web',
    bankDetails: 'Datos Bancarios',
    iban: 'IBAN',
    bankName: 'Nombre del Banco',
    invoiceSettings: 'Configuración de Facturas',
    invoicePrefix: 'Prefijo de Factura',
    nextInvoiceNumber: 'Próximo Número de Factura',
    save: 'Guardar Configuración',
    saving: 'Guardando...',
    saved: 'Configuración guardada correctamente',
    error: 'Error al guardar configuración',
    lastUpdated: 'Última actualización'
  }
};

const SettingsManagement = () => {
  const [lang, setLang] = useState('es');
  const labels = t[lang];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    business_name: 'PrimeDigital',
    legal_name: 'Mallorca Prime SL',
    cif_nif: '',
    address: '',
    city: 'Palma de Mallorca',
    postal_code: '',
    country: 'España',
    email: 'info@primedigital.es',
    phone: '+34 633 368 186',
    website: 'https://primedigital.es',
    iban: '',
    bank_name: '',
    invoice_prefix: 'INV',
    next_invoice_number: 1,
    logo_url: 'https://customer-assets.emergentagent.com/job_agency-services-4/artifacts/6niw4y6x_Gemini_Generated_Image_4afo654afo654afo%20copy.png',
    updated_at: null
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success(labels.saved);
        loadSettings();
      } else {
        toast.error(labels.error);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(labels.error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Settings className="w-8 h-8 text-[#D4AF37]" />
            <span>{labels.title}</span>
          </h1>
          <p className="text-gray-400 mt-1">{labels.subtitle}</p>
        </div>
        
        {/* Language Toggle */}
        <div className="flex space-x-2">
          <button
            onClick={() => setLang('es')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              lang === 'es' ? 'bg-[#D4AF37] text-[#141619]' : 'bg-[#2a3441] text-gray-400 hover:text-white'
            }`}
          >
            ES
          </button>
          <button
            onClick={() => setLang('en')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              lang === 'en' ? 'bg-[#D4AF37] text-[#141619]' : 'bg-[#2a3441] text-gray-400 hover:text-white'
            }`}
          >
            EN
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo & Branding */}
        <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] p-6 lg:col-span-2">
          <h2 className="text-xl font-bold text-white flex items-center space-x-2 mb-6">
            <Settings className="w-5 h-5 text-[#D4AF37]" />
            <span>{lang === 'es' ? 'Logo y Marca' : 'Logo & Branding'}</span>
          </h2>
          
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
            {/* Logo Preview */}
            <div className="p-4 bg-[#0a0c0f] rounded-xl border border-[#2a3441]">
              <img 
                src={settings.logo_url} 
                alt="Logo Preview" 
                className="h-16 w-auto object-contain"
                onError={(e) => e.target.src = 'https://via.placeholder.com/200x64?text=Logo'}
              />
            </div>
            
            {/* Logo URL Input */}
            <div className="flex-1 w-full">
              <label className="block text-sm text-gray-400 mb-1">
                {lang === 'es' ? 'URL del Logo' : 'Logo URL'}
              </label>
              <input
                type="url"
                value={settings.logo_url}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-gray-500 mt-1">
                {lang === 'es' ? 'Sube tu logo a un servicio de imágenes y pega la URL aquí' : 'Upload your logo to an image service and paste the URL here'}
              </p>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] p-6">
          <h2 className="text-xl font-bold text-white flex items-center space-x-2 mb-6">
            <Building className="w-5 h-5 text-[#D4AF37]" />
            <span>{labels.businessInfo}</span>
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{labels.businessName}</label>
              <input
                type="text"
                value={settings.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">{labels.legalName}</label>
              <input
                type="text"
                value={settings.legal_name}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                placeholder="Mallorca Prime SL"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">{labels.cifNif} *</label>
              <input
                type="text"
                value={settings.cif_nif}
                onChange={(e) => handleChange('cif_nif', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                placeholder="B12345678"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">{labels.address}</label>
              <input
                type="text"
                value={settings.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.city}</label>
                <input
                  type="text"
                  value={settings.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.postalCode}</label>
                <input
                  type="text"
                  value={settings.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">{labels.country}</label>
              <input
                type="text"
                value={settings.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Contact & Bank Details */}
        <div className="space-y-6">
          {/* Contact */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] p-6">
            <h2 className="text-xl font-bold text-white flex items-center space-x-2 mb-6">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              <span>Contacto</span>
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.email}</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.phone}</label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.website}</label>
                <input
                  type="url"
                  value={settings.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] p-6">
            <h2 className="text-xl font-bold text-white flex items-center space-x-2 mb-6">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              <span>{labels.bankDetails}</span>
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.iban}</label>
                <input
                  type="text"
                  value={settings.iban}
                  onChange={(e) => handleChange('iban', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                  placeholder="ES00 0000 0000 0000 0000 0000"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">{labels.bankName}</label>
                <input
                  type="text"
                  value={settings.bank_name}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] p-6">
            <h2 className="text-xl font-bold text-white flex items-center space-x-2 mb-6">
              <FileText className="w-5 h-5 text-[#8B5CF6]" />
              <span>{labels.invoiceSettings}</span>
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{labels.invoicePrefix}</label>
                  <input
                    type="text"
                    value={settings.invoice_prefix}
                    onChange={(e) => handleChange('invoice_prefix', e.target.value)}
                    className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                    placeholder="INV"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{labels.nextInvoiceNumber}</label>
                  <input
                    type="number"
                    value={settings.next_invoice_number}
                    onChange={(e) => handleChange('next_invoice_number', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-[#0a0c0f] border border-[#2a3441] rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-[#0a0c0f] rounded-lg border border-[#2a3441]">
                <p className="text-sm text-gray-400">
                  {lang === 'es' ? 'Próxima factura:' : 'Next invoice:'}{' '}
                  <span className="text-[#D4AF37] font-mono">
                    {settings.invoice_prefix}-{new Date().getFullYear()}-{String(settings.next_invoice_number).padStart(3, '0')}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-4 border-t border-[#2a3441]">
        {settings.updated_at && (
          <p className="text-sm text-gray-500">
            {labels.lastUpdated}: {new Date(settings.updated_at).toLocaleString()}
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-lg font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          <span>{saving ? labels.saving : labels.save}</span>
        </button>
      </div>
    </div>
  );
};

export default SettingsManagement;
